/**
 * Daily Intro — pure logic
 * The once-a-day, first-login "moment" that gates the app until the user
 * finishes their wellness check-in. All decision + data-shaping logic lives
 * here so the animated experience component stays presentational.
 */

import { bandForHour, pick, SALUTATIONS, SUBLINES } from './introCopy';

export type IntroPhase = 'boot' | 'greet' | 'mood' | 'states' | 'reveal' | 'exit';

export const INTRO_SOURCE = 'daily-intro';

export interface MoodOption {
  value: 'very_negative' | 'negative' | 'neutral' | 'positive' | 'very_positive';
  /** Canonical label — what gets submitted. The screen shows a rotating one (introCopy). */
  label: string;
  score: number;
  color: string;
}

// Same five values the wellness backend maps to scores 1–5.
export const MOOD_OPTIONS: MoodOption[] = [
  { value: 'very_negative', label: 'Heavy', score: 1, color: '#EF4444' },
  { value: 'negative', label: 'Meh', score: 2, color: '#F59E0B' },
  { value: 'neutral', label: 'Okay', score: 3, color: '#94A3B8' },
  { value: 'positive', label: 'Good', score: 4, color: '#3B82F6' },
  { value: 'very_positive', label: 'Great', score: 5, color: '#10B981' },
];

// Moodlet chips — identical vocabulary to WellnessCheckInModal so the
// backend's tag-derived energy/stress indices keep working.
export const STATE_OPTIONS = [
  'Stressed', 'Tired', 'Focused', 'Overwhelmed', 'Motivated',
  'Anxious', 'Calm', 'Energized', 'Frustrated', 'Content',
  'Burned out', 'Inspired', 'Confused', 'Confident', 'Worried',
];

export interface Greeting {
  salutation: string;
  subline: string;
}

/**
 * Time-of-day greeting drawn from the copy pools. `roll` in [0, 1) selects
 * the line; the default 0 returns the canonical first entry of each pool.
 */
export function greetingForHour(hour: number, roll: number = 0): Greeting {
  const band = bandForHour(hour);
  return {
    salutation: pick(SALUTATIONS[band], roll),
    subline: pick(SUBLINES[band], roll),
  };
}

/** localStorage key marking the intro handled for this user + local date.
 * Version bumped v1→v2 with the glassmorphism redesign so marks written by
 * the first release don't suppress the new experience on redeploy day. */
export function introMarkKey(userId: string | number, dateISO: string): string {
  return `mmai.dailyIntro.v2.${userId}.${dateISO}`;
}

export function localDateISO(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export type IntroMark = 'done' | 'skipped' | null;

export function readIntroMark(userId: string | number, dateISO: string): IntroMark {
  try {
    const v = window.localStorage.getItem(introMarkKey(userId, dateISO));
    return v === 'done' || v === 'skipped' ? v : null;
  } catch {
    return null;
  }
}

export function writeIntroMark(userId: string | number, dateISO: string, mark: Exclude<IntroMark, null>): void {
  try {
    window.localStorage.setItem(introMarkKey(userId, dateISO), mark);
  } catch {
    // storage unavailable — the server-side hasCheckedInToday still gates.
  }
}

export interface GateInput {
  role?: string;
  mustChangePassword?: boolean;
  pathname: string;
  mark: IntroMark;
  hasCheckedInToday: boolean | null; // null = status unknown / errored
}

/**
 * Whether the full-screen intro should gate the app right now.
 * Fails OPEN: unknown status (API error) never locks the user out.
 */
export function shouldShowIntro(input: GateInput): boolean {
  if (input.role === 'super-super-admin') return false; // platform admin console, not an employee day-loop
  if (input.mustChangePassword) return false; // password change takes priority
  if (input.pathname === '/questionnaire') return false; // onboarding flow owns the screen
  if (input.mark === 'done' || input.mark === 'skipped') return false;
  return input.hasCheckedInToday === false;
}

export interface CheckInAnswer {
  questionId: string;
  prompt: string;
  choice?: string;
  response?: string;
}

/** Same answer shape the existing WellnessCheckInModal submits. */
export function buildAnswers(mood: string, moodLabel: string, states: string[]): CheckInAnswer[] {
  return [
    { questionId: 'q1', prompt: 'How are you feeling today?', choice: mood, response: moodLabel },
    { questionId: 'q2', prompt: 'What best describes your current state?', choice: states.join(', '), response: states.join(', ') },
  ];
}

export interface Technique {
  title: string;
  description: string;
  action: string;
}

/** Deterministic activity suggestions when the AI endpoint is slow or down. */
export function fallbackTechniques(mood: string): Technique[] {
  const map: Record<string, Technique[]> = {
    very_negative: [
      { title: '5-4-3-2-1 grounding', description: 'A quick way to feel present when things are heavy.', action: 'Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste.' },
      { title: 'Reach out', description: 'Talking to someone can make a real difference.', action: 'Message a trusted colleague or friend — even just to say hi.' },
      { title: 'Step away for 15', description: 'Sometimes distance is the best first move.', action: 'Take 15 minutes away from the screen. Walk, breathe, reset.' },
    ],
    negative: [
      { title: 'Box breathing', description: 'A simple technique to lower stress fast.', action: 'Breathe in 4, hold 4, out 4, hold 4. Repeat four times.' },
      { title: 'Shrink the list', description: 'Overwhelm fades when the list gets honest.', action: 'Pick your top 3 for today. Park everything else.' },
      { title: 'Move your body', description: 'Movement shifts your mental state.', action: 'A 10-minute walk or a few stretches between meetings.' },
    ],
    neutral: [
      { title: 'Gratitude x3', description: 'Nudge an okay day toward a good one.', action: 'Write down 3 things you are grateful for today.' },
      { title: 'One mindful pause', description: 'Small pauses compound.', action: 'Take 5 quiet minutes before your next task.' },
      { title: 'Say hello', description: 'Connection is the fastest mood lift.', action: 'Have one real conversation with a colleague today.' },
    ],
    positive: [
      { title: 'Capture what works', description: 'Good days leave clues.', action: 'Jot down what is contributing to your mood today.' },
      { title: 'Share the energy', description: 'Recognition doubles when given away.', action: 'Recognise a colleague or share a win with your team.' },
      { title: 'Ride the momentum', description: 'Use the energy on something meaty.', action: 'Tackle your hardest task while you feel strong.' },
    ],
    very_positive: [
      { title: 'Celebrate it', description: 'Great days deserve a marker.', action: 'Acknowledge what you have accomplished lately.' },
      { title: 'Pay it forward', description: 'Lift someone else while you are up.', action: 'Offer help to a colleague who could use it.' },
      { title: 'Aim higher', description: 'Channel the energy into growth.', action: 'Set one stretch goal for the week ahead.' },
    ],
  };
  return map[mood] || map.neutral;
}

export interface TrendDay {
  date: string; // local ISO date
  score: number | null; // 1–5, null = no check-in that day
}

export interface TrendDigest {
  days: TrendDay[];
  direction: 'up' | 'down' | 'flat' | 'none';
  text: string;
}

/**
 * Normalize the wellness summary series into exactly `windowDays` calendar
 * days ending today, then read a direction from first-half vs second-half
 * averages. Series order/shape is defensive: entries may arrive in either
 * order and dates may be full timestamps.
 */
export function buildTrendDigest(
  series: Array<{ date: string; score: number }>,
  todayISO: string,
  windowDays: number = 7
): TrendDigest {
  const byDate = new Map<string, number>();
  for (const item of series || []) {
    if (!item || typeof item.score !== 'number' || item.score <= 0) continue;
    const day = String(item.date).slice(0, 10);
    byDate.set(day, item.score); // later entries win; one score per day
  }

  const days: TrendDay[] = [];
  const end = new Date(`${todayISO}T00:00:00`);
  for (let i = windowDays - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    const iso = localDateISO(d);
    days.push({ date: iso, score: byDate.has(iso) ? (byDate.get(iso) as number) : null });
  }

  const scored = days.filter((d) => d.score !== null) as Array<{ date: string; score: number }>;
  if (scored.length < 2) {
    return { days, direction: 'none', text: 'Your trend starts today — check in daily to see it grow.' };
  }

  const half = Math.floor(scored.length / 2);
  const avg = (xs: Array<{ score: number }>) => xs.reduce((s, x) => s + x.score, 0) / xs.length;
  const first = avg(scored.slice(0, half));
  const second = avg(scored.slice(half));
  const delta = second - first;

  if (delta > 0.2) return { days, direction: 'up', text: 'Your mood has been trending up this week.' };
  if (delta < -0.2) return { days, direction: 'down', text: 'The week has been a little heavier — be kind to yourself.' };
  return { days, direction: 'flat', text: 'Your mood has been steady this week.' };
}
